import React, { useEffect, useState, useCallback } from "react"
import { useParams, useNavigate } from "react-router-dom"
import { Button, Form, Alert, Badge, Breadcrumb, Card, Row, Col, Spinner, Modal, Table } from "react-bootstrap"
import { FaArrowLeft, FaLock, FaLockOpen, FaTimes, FaTrash } from "react-icons/fa"
import CreatableSelect from "react-select/creatable"
import useFetch from "../../../_fetch"
import { getClickValuePeriodError } from "../link-script/partnerships/click-value-period-validation"

type ClickValuePeriodDraft = { startDate: string; endDate: string; value: string }

interface PublisherUser {
    username: string
    userId: string
    addedAt: string
    active: boolean
}

interface Advertiser {
    username: string
    userId: string
    addedAt: string
}

interface Partnership {
    id: string
    name: string
    type: string
    mode: string
    value: number
    currency: string
    startDate: string
    expiryDate: string | null
    cookieDays: number
    createdAt: string
    domains: string[]
    locked?: boolean
    archivedAt?: string | null
    deletedAt?: string | null
    clickValuePeriods?: Array<{ startDate: string; endDate: string; value: number }>
    scriptSecret?: string
    scriptKeyVersion?: number
    publisher_users: PublisherUser[]
    advertiser?: Advertiser
}

function cleanDomain(input: string): string | null {
    try {
        const url = input.startsWith("http") ? input : `https://${input}`
        const parsed = new URL(url)
        return parsed.hostname.toLowerCase().replace(/^www\d*\./i, "")
    } catch {
        return null
    }
}

function isValidDomain(domain: string) {
    return /^[a-z0-9.-]+\.[a-z]{2,}$/i.test(domain)
}

function minDate(...values: Array<string | null | undefined>) {
    return values.filter((value): value is string => !!value).sort()[0] || undefined
}

function maxDate(...values: Array<string | null | undefined>) {
    const dates = values.filter((value): value is string => !!value).sort()
    return dates[dates.length - 1] || undefined
}

function shiftDate(value: string | null | undefined, days: number) {
    if (!value) return undefined
    const date = new Date(`${value}T00:00:00.000Z`)
    if (Number.isNaN(date.getTime())) return undefined
    date.setUTCDate(date.getUTCDate() + days)
    return date.toISOString().slice(0, 10)
}

function getAdminLifecycle(partnership: Partnership) {
    if (partnership.deletedAt) return "Deleted"
    if (partnership.archivedAt) return "Archived"
    const hasPublisher = partnership.publisher_users[0]?.active !== false && !!partnership.publisher_users[0]
    if (!hasPublisher) return "Awaiting publisher"
    if (partnership.expiryDate && new Date(`${partnership.expiryDate}T23:59:59.999`) < new Date()) return "Expired"
    return "Active"
}

function getPayout(partnership: Partnership) {
    return partnership.type === "sale"
        ? `${partnership.value}%`
        : `${partnership.value} ${partnership.currency}`
}

function getPayoutDescription(partnership: Partnership) {
    if (partnership.type === "sale") return "Percentage of approved order value"
    if (partnership.type === "click") return "Default value per click"
    return "Value per approved lead"
}

const PartnershipAgreementDetail: React.FC = () => {
    const { id } = useParams<{ id: string }>()
    const navigate = useNavigate()
    const { doFetch } = useFetch()

    const [partnership, setPartnership] = useState<Partnership | null>(null)
    const [publisherUsernames, setPublisherUsernames] = useState<string[]>([])
    const [advertiserUsernames, setAdvertiserUsernames] = useState<string[]>([])
    const [loading, setLoading] = useState(true)
    const [error, setError] = useState<string | null>(null)

    // Settings form
    const [name, setName] = useState("")
    const [type, setType] = useState("")
    const [value, setValue] = useState("")
    const [currency, setCurrency] = useState("")
    const [startDate, setStartDate] = useState("")
    const [expiryDate, setExpiryDate] = useState("")
    const [cookieDays, setCookieDays] = useState("")
    const [locked, setLocked] = useState(false)
    const [domains, setDomains] = useState<string[]>([])
    const [domainError, setDomainError] = useState<string | null>(null)
    const [saveLoading, setSaveLoading] = useState(false)
    const [saveError, setSaveError] = useState<string | null>(null)
    const [saveSuccess, setSaveSuccess] = useState(false)
    const [clickValuePeriods, setClickValuePeriods] = useState<ClickValuePeriodDraft[]>([])
    const [periodSaving, setPeriodSaving] = useState(false)
    const [periodError, setPeriodError] = useState("")
    const [periodSuccess, setPeriodSuccess] = useState("")

    // Add publisher
    const [draftPublisherUsername, setDraftPublisherUsername] = useState("")

    // Set advertiser
    const [draftAdvertiserUsername, setDraftAdvertiserUsername] = useState("")
    const [showRotateSecret, setShowRotateSecret] = useState(false)
    const [rotateSecretLoading, setRotateSecretLoading] = useState(false)
    const [rotatedSecret, setRotatedSecret] = useState("")
    const [rotateSecretError, setRotateSecretError] = useState("")
    const [restoreLoading, setRestoreLoading] = useState(false)
    const [lifecycleLoading, setLifecycleLoading] = useState(false)
    const [showLifecycleAction, setShowLifecycleAction] = useState(false)
    const [lifecycleImpact, setLifecycleImpact] = useState<{ conversions: number; clicks: number; hasActivity: boolean; requiresArchive: boolean } | null>(null)
    const [lifecycleError, setLifecycleError] = useState("")

    const populate = (data: Partnership) => {
        setPartnership(data)
        setName(data.name || "")
        setType(data.type || "")
        setValue(String(data.value ?? ""))
        setCurrency(data.currency || "")
        setStartDate(data.startDate || "")
        setExpiryDate(data.expiryDate || "")
        setCookieDays(String(data.cookieDays ?? ""))
        setLocked(!!data.locked)
        setDomains(Array.isArray(data.domains) ? data.domains : [])
        setClickValuePeriods((data.clickValuePeriods || []).map((period) => ({ ...period, value: String(period.value) })))
        setDraftPublisherUsername(data.publisher_users[0]?.username || "")
        setDraftAdvertiserUsername(data.advertiser?.username || "")
    }

    const fetchPartnership = useCallback(async () => {
        setLoading(true)
        setError(null)
        try {
            const res = await doFetch(`/user/admin/partnerships/${id}`)
            if (!res.ok) throw new Error(await res.text())
            populate(await res.json())
        } catch (err: any) {
            setError(err?.message || "Failed to load partnership")
        } finally {
            setLoading(false)
        }
    }, [doFetch, id])

    useEffect(() => {
        fetchPartnership()
        doFetch("/user/get-users")
            .then((r) => (r.ok ? r.json() : []))
            .then((users: any[]) => {
                const publishers = users
                    .filter((u) => u.kind === "publisher" && !u.isAdmin)
                    .map((u) => u.username)
                    .sort()
                const advertisers = users
                    .filter((u) => u.kind === "advertiser" && !u.isAdmin)
                    .map((u) => u.username)
                    .sort()
                setPublisherUsernames(publishers)
                setAdvertiserUsernames(advertisers)
            })
            .catch(() => {})
    }, [doFetch, fetchPartnership])

    const handleAddDomain = (option: any) => {
        const raw = option?.value?.trim()?.toLowerCase()
        if (!raw) return
        const cleaned = cleanDomain(raw)
        if (!cleaned || !isValidDomain(cleaned)) {
            setDomainError("Invalid domain")
            return
        }
        setDomainError(null)
        setDomains((prev) => (prev.includes(cleaned) ? prev : [...prev, cleaned]))
    }

    const handleRemoveDomain = (domain: string) => {
        setDomains((prev) => prev.filter((d) => d !== domain))
    }

    const handleSave = async () => {
        setSaveLoading(true)
        setSaveError(null)
        setSaveSuccess(false)
        try {
            const cookieDaysValue = Number(cookieDays)
            if (type !== "click" && (!Number.isInteger(cookieDaysValue) || cookieDaysValue < 1 || cookieDaysValue > 60)) {
                setSaveError("Cookie days must be an integer between 1 and 60")
                return
            }

            const res = await doFetch(
                `/user/admin/partnerships/${id}`,
                {
                    name,
                    type,
                    value: Number(value),
                    currency,
                    startDate,
                    expiryDate: expiryDate || null,
                    cookieDays: Number(cookieDays),
                    locked,
                    domains,
                    publisherUsername: draftPublisherUsername || null,
                    advertiserUsername: draftAdvertiserUsername || null,
                },
                "PUT"
            )
            if (!res.ok) {
                const errBody = await res.json().catch(() => null)
                setSaveError(errBody?.error || "Failed to save")
                return
            }
            const updated = await res.json()
            populate({ ...updated, scriptSecret: partnership?.scriptSecret })
            setSaveSuccess(true)
        } catch (err: any) {
            setSaveError(err?.message || "Failed to save")
        } finally {
            setSaveLoading(false)
        }
    }

    const updateClickValuePeriod = (index: number, patch: Partial<ClickValuePeriodDraft>) => {
        setPeriodError("")
        setPeriodSuccess("")
        setClickValuePeriods((periods) => periods.map((period, periodIndex) => periodIndex === index ? { ...period, ...patch } : period))
    }

    const addClickValuePeriod = () => {
        setPeriodError("")
        setPeriodSuccess("")
        setClickValuePeriods((periods) => [...periods, { startDate: "", endDate: "", value: String(partnership?.value ?? "") }])
    }

    const removeClickValuePeriod = (index: number) => {
        setPeriodError("")
        setPeriodSuccess("")
        setClickValuePeriods((periods) => periods.filter((_, periodIndex) => periodIndex !== index))
    }

    const saveClickValuePeriods = async () => {
        if (!partnership) return
        const validationError = getClickValuePeriodError(partnership.startDate, partnership.expiryDate || "", clickValuePeriods)
        if (validationError) {
            setPeriodError(validationError)
            setPeriodSuccess("")
            return
        }
        setPeriodSaving(true)
        setPeriodError("")
        setPeriodSuccess("")
        try {
            const res = await doFetch(`/user/admin/partnerships/${partnership.id}`, {
                clickValuePeriods: clickValuePeriods.map((period) => ({ ...period, value: Number(period.value) })),
            }, "PUT")
            if (!res.ok) {
                const body = await res.json().catch(() => null)
                throw new Error(body?.error || "Failed to save CPC periods")
            }
            const updated: Partnership = await res.json()
            setPartnership(updated)
            setClickValuePeriods((updated.clickValuePeriods || []).map((period) => ({ ...period, value: String(period.value) })))
            setPeriodSuccess("CPC periods saved")
        } catch (err: any) {
            setPeriodError(err?.message || "Failed to save CPC periods")
        } finally {
            setPeriodSaving(false)
        }
    }

    const handleRemovePublisher = () => {
        setDraftPublisherUsername("")
        setSaveSuccess(false)
    }

    const handleRemoveAdvertiser = () => {
        setDraftAdvertiserUsername("")
        setLocked(false)
        setSaveSuccess(false)
    }

    const handleRotateSecret = async () => {
        if (!partnership) return
        setRotateSecretLoading(true)
        setRotateSecretError("")
        try {
            const res = await doFetch(`/user/admin/partnerships/${partnership.id}/regenerate-secret`, {})
            if (!res.ok) {
                const body = await res.json().catch(() => null)
                throw new Error(body?.error || "Failed to rotate secret")
            }
            const body = await res.json()
            setRotatedSecret(body.scriptSecret || "")
        } catch (err: any) {
            setRotateSecretError(err?.message || "Failed to rotate secret")
        } finally {
            setRotateSecretLoading(false)
        }
    }

    const closeRotateSecret = () => {
        const shouldRefresh = !!rotatedSecret
        setShowRotateSecret(false)
        setRotatedSecret("")
        setRotateSecretError("")
        if (shouldRefresh) void fetchPartnership()
    }

    const handleRestore = async () => {
        if (!partnership) return
        setRestoreLoading(true)
        setError(null)
        try {
            const res = await doFetch(`/user/partnerships/${partnership.id}/restore`, {}, "POST")
            if (!res.ok) throw new Error((await res.json().catch(() => null))?.error || "Failed to restore partnership")
            await fetchPartnership()
        } catch (err: any) {
            setError(err?.message || "Failed to restore partnership")
        } finally {
            setRestoreLoading(false)
        }
    }

    const inspectLifecycleImpact = async () => {
        if (!partnership) return
        setLifecycleLoading(true)
        setLifecycleError("")
        try {
            const res = await doFetch(`/user/partnerships/${partnership.id}/deletion-impact`)
            if (!res.ok) throw new Error((await res.json().catch(() => null))?.error || "Failed to inspect partnership activity")
            setLifecycleImpact(await res.json())
            setShowLifecycleAction(true)
        } catch (err: any) {
            setLifecycleError(err?.message || "Failed to inspect partnership activity")
        } finally {
            setLifecycleLoading(false)
        }
    }

    const handleLifecycleAction = async () => {
        if (!partnership || !lifecycleImpact) return
        setLifecycleLoading(true)
        setLifecycleError("")
        try {
            const res = lifecycleImpact.requiresArchive
                ? await doFetch(`/user/partnerships/${partnership.id}/archive`, {}, "POST")
                : await doFetch(`/user/partnerships/${partnership.id}`, undefined, "DELETE")
            if (!res.ok) throw new Error((await res.json().catch(() => null))?.error || "Failed to update partnership")
            setShowLifecycleAction(false)
            if (lifecycleImpact.requiresArchive) await fetchPartnership()
            else navigate("/partnership-agreements")
        } catch (err: any) {
            setLifecycleError(err?.message || "Failed to update partnership")
        } finally {
            setLifecycleLoading(false)
        }
    }

    if (loading) {
        return (
            <div className="p-4 d-flex justify-content-center">
                <Spinner animation="border" />
            </div>
        )
    }

    if (error || !partnership) {
        return (
            <div className="p-4">
                <Alert variant="danger">{error || "Partnership not found"}</Alert>
                <Button variant="outline-secondary" size="sm" onClick={() => navigate("/partnership-agreements")}>
                    <FaArrowLeft className="me-1" /> Back
                </Button>
            </div>
        )
    }

    const lifecycle = getAdminLifecycle(partnership)
    const primaryPublisher = partnership.publisher_users[0]
    const activePublisher = primaryPublisher?.active !== false ? primaryPublisher : undefined
    const periodStarts = (partnership.clickValuePeriods || []).map((period) => period.startDate).filter(Boolean).sort()
    const periodEnds = (partnership.clickValuePeriods || []).map((period) => period.endDate).filter(Boolean).sort()
    const earliestPeriodStart = periodStarts[0]
    const latestPeriodEnd = periodEnds[periodEnds.length - 1]
    const startDateMax = [expiryDate, earliestPeriodStart].filter(Boolean).sort()[0] || undefined
    const expiryDateMin = [startDate, latestPeriodEnd].filter(Boolean).sort().slice(-1)[0] || undefined
    const livePeriodError = partnership.type === "click"
        ? getClickValuePeriodError(partnership.startDate, partnership.expiryDate || "", clickValuePeriods)
        : ""

    return (
        <div className="container-fluid px-2 px-md-3 partnership-page-shell">
            <Card className="partnership-hero-card mb-4 border-0">
                <Card.Body>
                <Breadcrumb className="mb-3 small">
                    <Breadcrumb.Item
                        href="/partnership-agreements"
                        onClick={(event) => {
                            event.preventDefault()
                            navigate("/partnership-agreements")
                        }}
                    >
                        Partnership agreements
                    </Breadcrumb.Item>
                    <Breadcrumb.Item active>{partnership.name}</Breadcrumb.Item>
                </Breadcrumb>
                <div className="d-flex flex-column flex-lg-row justify-content-between gap-3">
                    <div>
                        <div className="d-flex flex-wrap align-items-center gap-2 mb-2">
                            <h4 className="mb-0">{partnership.name}</h4>
                            <Badge bg={partnership.type === "sale" ? "primary" : partnership.type === "click" ? "info" : "warning"}>
                                {partnership.type.charAt(0).toUpperCase() + partnership.type.slice(1)}
                            </Badge>
                            <Badge bg={lifecycle === "Active" ? "success" : lifecycle === "Expired" || lifecycle === "Archived" || lifecycle === "Deleted" ? "secondary" : "warning"}>
                                {lifecycle}
                            </Badge>
                            {partnership.locked && <Badge bg="dark"><FaLock className="me-1" /> Advertiser locked</Badge>}
                        </div>
                        <div className="text-muted small">
                            Agreement ID: <code>{partnership.id}</code>
                            {partnership.createdAt && <> · Created {new Date(partnership.createdAt).toLocaleDateString()}</>}
                        </div>
                    </div>
                    <div className="text-lg-end text-muted small align-self-lg-end">{partnership.archivedAt ? <Button size="sm" variant="outline-primary" disabled={restoreLoading} onClick={handleRestore}>{restoreLoading ? "Restoring…" : "Restore partnership"}</Button> : "Manage agreement settings and participant access"}</div>
                </div>
                </Card.Body>
            </Card>

            <Row className="g-3 mb-4">
                <Col xs={12} sm={6} xl={3}>
                    <Card className="partnership-kpi-card h-100 border-0"><Card.Body>
                        <div className="text-muted small mb-1">Payout</div>
                        <div className="fs-5 fw-semibold">{getPayout(partnership)}</div>
                        <div className="text-muted small">{getPayoutDescription(partnership)}</div>
                    </Card.Body></Card>
                </Col>
                <Col xs={12} sm={6} xl={3}>
                    <Card className="partnership-kpi-card h-100 border-0"><Card.Body>
                        <div className="text-muted small mb-1">Publisher</div>
                        <div className="fs-5 fw-semibold text-truncate">{activePublisher?.username || "Not assigned"}</div>
                        <div className="text-muted small">Primary partner</div>
                    </Card.Body></Card>
                </Col>
                <Col xs={12} sm={6} xl={3}>
                    <Card className="partnership-kpi-card h-100 border-0"><Card.Body>
                        <div className="text-muted small mb-1">Advertiser</div>
                        <div className="fs-5 fw-semibold text-truncate">{partnership.advertiser?.username || "Not assigned"}</div>
                        <div className="text-muted small">{partnership.locked ? "Publisher editing disabled" : "Standard access"}</div>
                    </Card.Body></Card>
                </Col>
                <Col xs={12} sm={6} xl={3}>
                    <Card className="partnership-kpi-card h-100 border-0"><Card.Body>
                        <div className="text-muted small mb-1">Period</div>
                        <div className="fs-6 fw-semibold">{partnership.startDate || "No start date"}</div>
                        <div className="text-muted small">to {partnership.expiryDate || "No expiry"}</div>
                    </Card.Body></Card>
                </Col>
            </Row>

            {partnership.type !== "click" && <Card className="mb-4 border-0 shadow-sm partnership-panel"><Card.Body>
                <div className="d-flex flex-column flex-lg-row justify-content-between align-items-lg-end gap-3">
                    <div className="flex-grow-1" style={{ minWidth: 0 }}>
                        <div className="partnership-section-title">Conversion Secret Key</div>
                        <div className="text-muted small mt-1 mb-2">Use this key for the advertiser integration. Treat it as a password.</div>
                        <Form.Control aria-label="Conversion Secret Key" className="font-monospace" value={partnership.scriptSecret || ""} readOnly onFocus={(event) => event.currentTarget.select()} />
                    </div>
                    <div className="d-flex flex-column flex-sm-row gap-2 partnership-secret-actions">
                        <Button variant="outline-secondary" disabled={!partnership.scriptSecret} onClick={() => partnership.scriptSecret && navigator.clipboard?.writeText(partnership.scriptSecret)}>Copy key</Button>
                        <Button variant="outline-danger" disabled={!!partnership.archivedAt || !!partnership.deletedAt} onClick={() => setShowRotateSecret(true)}>Rotate key</Button>
                    </div>
                </div>
            </Card.Body></Card>}

            {/* ── Settings card ──────────────────────────────────────── */}
            <Card className="mb-4 border-0 shadow-sm partnership-panel">
                <Card.Header className="bg-white border-0 pt-4 px-4 pb-0">
                    <div className="partnership-section-title">Agreement settings</div>
                    <div className="text-muted small mt-1">Commercial terms, tracking setup and editing access.</div>
                </Card.Header>
                <Card.Body>
                    <Row className="g-3">
                        <Col sm={12}>
                            <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Name</Form.Label>
                            <Form.Control value={name} onChange={(e) => setName(e.target.value)} size="sm" />
                        </Col>
                        <Col sm={4}>
                            <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Type</Form.Label>
                            <Form.Select value={type} onChange={(e) => setType(e.target.value)} size="sm" disabled>
                                <option value="sale">Sale</option>
                                <option value="click">Click</option>
                                <option value="lead">Lead</option>
                            </Form.Select>
                        </Col>
                        <Col sm={4}>
                            <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Currency</Form.Label>
                            <Form.Control
                                value={currency}
                                onChange={(e) => setCurrency(e.target.value.toUpperCase())}
                                maxLength={3}
                                size="sm"
                                placeholder="DKK"
                            />
                        </Col>
                        <Col sm={4}>
                            <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>
                                {type === "click" ? "Default value per click" : type === "sale" ? "Sale percentage" : "Value per lead"} {type === "sale" ? "(%)" : `(${currency || "DKK"})`}
                            </Form.Label>
                            <Form.Control
                                type="number"
                                value={value}
                                onChange={(e) => setValue(e.target.value)}
                                size="sm"
                                min={0}
                                step={0.01}
                            />
                        </Col>
                        <Col sm={4}>
                            <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Start date</Form.Label>
                            <Form.Control
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                size="sm"
                                max={startDateMax}
                            />
                        </Col>
                        <Col sm={4}>
                            <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Expiry date</Form.Label>
                            <Form.Control
                                type="date"
                                value={expiryDate}
                                onChange={(e) => setExpiryDate(e.target.value)}
                                size="sm"
                                min={expiryDateMin}
                            />
                        </Col>
                        {type !== "click" && (
                            <Col sm={4}>
                                <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Cookie days</Form.Label>
                                <Form.Control
                                    type="number"
                                    value={cookieDays}
                                    onChange={(e) => setCookieDays(e.target.value)}
                                    size="sm"
                                    min={1}
                                    max={60}
                                />
                            </Col>
                        )}

                        {/* Domains */}
                        <Col sm={12}>
                            <Form.Label className="fw-semibold mb-1" style={{ fontSize: "0.85rem" }}>Partner domains</Form.Label>
                            <CreatableSelect
                                placeholder="Add domain (e.g. partner.com)"
                                isClearable
                                formatCreateLabel={(val) => `Add "${val}"`}
                                onChange={handleAddDomain}
                                onCreateOption={(val) => handleAddDomain({ value: val })}
                                value={null}
                            />
                            <div className="d-flex flex-wrap gap-2 mt-2">
                                {domains.map((d) => (
                                    <Badge key={d} bg="info" className="d-flex align-items-center px-2 py-1">
                                        {d}
                                        <FaTimes
                                            className="ms-2"
                                            style={{ cursor: "pointer" }}
                                            onClick={() => handleRemoveDomain(d)}
                                        />
                                    </Badge>
                                ))}
                            </div>
                            {domainError && <div className="text-danger small mt-2">{domainError}</div>}
                        </Col>

                        {/* Locked toggle — only visible when advertiser is set */}
                        {draftAdvertiserUsername && (
                            <Col sm={12}>
                                <Form.Check
                                    type="switch"
                                    id="locked-switch"
                                    label={
                                        <span className="d-flex align-items-center gap-1" style={{ fontSize: "0.85rem" }}>
                                            {locked
                                                ? <FaLock style={{ color: "#6c757d" }} />
                                                : <FaLockOpen style={{ color: "#6c757d" }} />}
                                            {locked ? "Locked — publishers cannot edit" : "Unlocked — publishers can edit"}
                                        </span>
                                    }
                                    checked={locked}
                                    onChange={(e) => setLocked(e.target.checked)}
                                />
                            </Col>
                        )}
                    </Row>
                </Card.Body>
            </Card>

            {partnership.type === "click" && <Card className="mb-4 border-0 shadow-sm partnership-panel">
                <Card.Header className="bg-white border-0 pt-4 px-4 pb-0 d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2 partnership-section-header">
                    <div>
                        <div className="partnership-section-title">CPC periods</div>
                        <div className="text-muted small mt-1">Override the default CPC for inclusive, non-overlapping date ranges.</div>
                    </div>
                    <Button size="sm" variant="outline-primary" onClick={addClickValuePeriod} disabled={periodSaving || !!partnership.archivedAt || !!partnership.deletedAt}>Add period</Button>
                </Card.Header>
                <Card.Body>
                    {periodSuccess && <Alert variant="success" dismissible onClose={() => setPeriodSuccess("")} className="py-2">{periodSuccess}</Alert>}
                    {clickValuePeriods.length > 0 ? <Table responsive size="sm" className="align-middle partnership-period-table mb-0">
                        <thead><tr><th>Start date</th><th>End date</th><th>Value per click</th><th>Currency</th><th /></tr></thead>
                        <tbody>{clickValuePeriods.map((period, index) => {
                            const previousEnd = clickValuePeriods[index - 1]?.endDate
                            const nextStart = clickValuePeriods[index + 1]?.startDate
                            const disabled = periodSaving || !!partnership.archivedAt || !!partnership.deletedAt
                            return <tr key={index}>
                                <td><Form.Control aria-label={`Admin CPC period ${index + 1} start date`} size="sm" type="date" value={period.startDate} min={maxDate(partnership.startDate, shiftDate(previousEnd, 1))} max={minDate(period.endDate, partnership.expiryDate, shiftDate(nextStart, -1))} disabled={disabled} onChange={(event) => updateClickValuePeriod(index, { startDate: event.target.value })} /></td>
                                <td><Form.Control aria-label={`Admin CPC period ${index + 1} end date`} size="sm" type="date" value={period.endDate} min={maxDate(partnership.startDate, period.startDate, shiftDate(previousEnd, 1))} max={minDate(partnership.expiryDate, shiftDate(nextStart, -1))} disabled={disabled} onChange={(event) => updateClickValuePeriod(index, { endDate: event.target.value })} /></td>
                                <td><Form.Control aria-label={`Admin CPC period ${index + 1} value`} size="sm" type="number" min={0} step="0.01" value={period.value} disabled={disabled} onChange={(event) => updateClickValuePeriod(index, { value: event.target.value })} /></td>
                                <td><span className="partnership-currency-pill">{partnership.currency}</span></td>
                                <td className="text-end"><Button size="sm" variant="link" className="text-danger text-decoration-none" disabled={disabled} onClick={() => removeClickValuePeriod(index)}>Remove</Button></td>
                            </tr>
                        })}</tbody>
                    </Table> : <div className="partnership-empty-state"><div className="fw-semibold mb-1">Default CPC applies to every click</div><div className="text-muted small">Add a period when a specific date range should use another value.</div></div>}
                    {(periodError || livePeriodError) && <Alert variant="warning" className="mt-3 mb-0">{periodError || livePeriodError}</Alert>}
                    <div className="mt-3 pt-3 border-top d-flex flex-column flex-sm-row justify-content-between align-items-sm-center gap-2">
                        <div className="text-muted small">CPC periods are saved independently from agreement data.</div>
                        <Button size="sm" variant="primary" onClick={saveClickValuePeriods} disabled={periodSaving || !!livePeriodError || !!partnership.archivedAt || !!partnership.deletedAt}>{periodSaving ? <Spinner animation="border" size="sm" /> : "Save CPC periods"}</Button>
                    </div>
                </Card.Body>
            </Card>}

            {/* ── Publisher card ─────────────────────────────────────── */}
            <Card className="mb-4 border-0 shadow-sm partnership-panel">
                <Card.Header className="bg-white border-0 pt-4 px-4 pb-0">
                    <div className="partnership-section-title">Publisher management</div>
                    <div className="text-muted small mt-1">Assign the single publisher that can use this agreement.</div>
                </Card.Header>
                <Card.Body>
                    {!draftPublisherUsername ? (
                        <p className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>No publisher added.</p>
                    ) : (
                        <div className="mb-3">
                            <div className="text-muted small mb-2">A partnership can only have one publisher. Remove the current publisher before assigning another.</div>
                            <div className="d-flex flex-wrap gap-2">
                                <div
                                    className="d-flex align-items-center gap-1 border rounded px-2 py-1"
                                    style={{
                                        fontSize: "0.82rem",
                                        background: "#e9ecef",
                                        color: "#343a40",
                                    }}
                                >
                                    <span style={{ fontSize: "0.78rem" }}>{draftPublisherUsername}</span>
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 text-danger"
                                        aria-label={`Remove publisher ${draftPublisherUsername}`}
                                        style={{ fontSize: "0.75rem", lineHeight: 1 }}
                                        title="Remove publisher"
                                        onClick={handleRemovePublisher}
                                        disabled={!!partnership.archivedAt || !!partnership.deletedAt}
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {!draftPublisherUsername && (
                        <Form.Group controlId="admin-publisher-assignment">
                            <Form.Label style={{ fontSize: "0.82rem" }} className="mb-1">Assign publisher</Form.Label>
                            <Form.Select
                                size="sm"
                                value=""
                                disabled={!!partnership.archivedAt || !!partnership.deletedAt}
                                onChange={(e) => {
                                    setDraftPublisherUsername(e.target.value)
                                    setSaveSuccess(false)
                                }}
                            >
                                <option value="">— select publisher —</option>
                                {publisherUsernames.map((u) => (
                                    <option key={u} value={u}>{u}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}
                </Card.Body>
            </Card>

            {/* ── Advertiser card ────────────────────────────────────── */}
            <Card className="mb-4 border-0 shadow-sm partnership-panel">
                <Card.Header className="bg-white border-0 pt-4 px-4 pb-0">
                    <div className="partnership-section-title">Advertiser management</div>
                    <div className="text-muted small mt-1">Assign ownership and control publisher editing access.</div>
                </Card.Header>
                <Card.Body>
                    {!draftAdvertiserUsername ? (
                        <p className="text-muted mb-2" style={{ fontSize: "0.85rem" }}>No advertiser assigned.</p>
                    ) : (
                        <div className="mb-3">
                            <div className="text-muted small mb-2">Remove the current advertiser before assigning another.</div>
                            <div className="d-flex flex-wrap gap-2">
                                <div
                                    className="d-flex align-items-center gap-1 border rounded px-2 py-1"
                                    style={{
                                        fontSize: "0.82rem",
                                        background: "#e9ecef",
                                        color: "#343a40",
                                    }}
                                >
                                    <span style={{ fontSize: "0.78rem" }}>{draftAdvertiserUsername}</span>
                                    <button
                                        type="button"
                                        className="btn btn-link p-0 text-danger"
                                        aria-label={`Remove advertiser ${draftAdvertiserUsername}`}
                                        style={{ fontSize: "0.75rem", lineHeight: 1 }}
                                        title="Remove advertiser"
                                        onClick={handleRemoveAdvertiser}
                                        disabled={!!partnership.archivedAt || !!partnership.deletedAt}
                                    >
                                        <FaTrash />
                                    </button>
                                </div>
                            </div>
                        </div>
                    )}
                    {!draftAdvertiserUsername && (
                        <Form.Group controlId="admin-advertiser-assignment">
                            <Form.Label style={{ fontSize: "0.82rem" }} className="mb-1">Assign advertiser</Form.Label>
                            <Form.Select
                                size="sm"
                                value=""
                                disabled={!!partnership.archivedAt || !!partnership.deletedAt}
                                onChange={(e) => {
                                    setDraftAdvertiserUsername(e.target.value)
                                    setSaveSuccess(false)
                                }}
                            >
                                <option value="">— select advertiser —</option>
                                {advertiserUsernames.map((u) => (
                                    <option key={u} value={u}>{u}</option>
                                ))}
                            </Form.Select>
                        </Form.Group>
                    )}
                </Card.Body>
            </Card>

            {!partnership.archivedAt && !partnership.deletedAt && (
                <Card className="mb-4 border-0 shadow-sm partnership-panel">
                    <Card.Body className="d-flex flex-column flex-md-row justify-content-between align-items-md-center gap-3">
                        <div>
                            <div className="fw-semibold">Save partnership changes</div>
                            <div className="text-muted small">Settings and participant changes are only applied when you save.</div>
                        </div>
                        <Button variant="primary" onClick={handleSave} disabled={saveLoading}>
                            {saveLoading ? <Spinner animation="border" size="sm" /> : "Save changes"}
                        </Button>
                    </Card.Body>
                    {(saveSuccess || saveError) && <Card.Footer className="border-0 bg-white pt-0">
                        {saveSuccess && <Alert variant="success" dismissible onClose={() => setSaveSuccess(false)} className="mb-0">Changes saved.</Alert>}
                        {saveError && <Alert variant="danger" dismissible onClose={() => setSaveError(null)} className="mb-0">{saveError}</Alert>}
                    </Card.Footer>}
                </Card>
            )}

            {!partnership.archivedAt && !partnership.deletedAt && <div className="d-flex flex-column align-items-end gap-2 mb-5 pt-2">
                {lifecycleError && <Alert variant="danger" className="py-2 mb-0">{lifecycleError}</Alert>}
                <Button size="sm" variant="outline-secondary" disabled={lifecycleLoading} onClick={inspectLifecycleImpact}>
                    {lifecycleLoading ? "Checking activity…" : "Archive or remove partnership"}
                </Button>
            </div>}

            <Modal show={showRotateSecret} onHide={closeRotateSecret} backdrop={rotatedSecret ? "static" : true}>
                <Modal.Header closeButton><Modal.Title>{rotatedSecret ? "New secret created" : "Rotate Conversion Secret Key"}</Modal.Title></Modal.Header>
                <Modal.Body>{rotatedSecret ? <><Alert variant="warning">The new key is active, and the previous integration key is already invalid.</Alert><Form.Control aria-label="New Conversion Secret Key" className="font-monospace mb-3" value={rotatedSecret} readOnly onFocus={(event) => event.currentTarget.select()} /><Button variant="outline-secondary" onClick={() => navigator.clipboard?.writeText(rotatedSecret)}>Copy key</Button></> : <><p>Rotating the key immediately stops integrations that still use the current key.</p><p className="mb-0 text-muted small">Continue only when the advertiser is ready to update the integration.</p></>}{rotateSecretError && <Alert variant="danger" className="mt-3 mb-0">{rotateSecretError}</Alert>}</Modal.Body>
                <Modal.Footer><Button variant="secondary" onClick={closeRotateSecret}>{rotatedSecret ? "Done" : "Cancel"}</Button>{!rotatedSecret && <Button variant="danger" disabled={rotateSecretLoading} onClick={handleRotateSecret}>{rotateSecretLoading ? "Rotating…" : "Rotate key"}</Button>}</Modal.Footer>
            </Modal>
            <Modal show={showLifecycleAction} onHide={() => setShowLifecycleAction(false)}>
                <Modal.Header closeButton><Modal.Title>{lifecycleImpact?.requiresArchive ? "Archive partnership" : "Delete partnership"}</Modal.Title></Modal.Header>
                <Modal.Body>{lifecycleImpact?.requiresArchive ? <><p>This partnership has recorded conversions and will be archived so all historical data remains available.</p><div className="rounded bg-light p-3"><div>Conversions: <strong>{lifecycleImpact.conversions}</strong></div><div>Clicks: <strong>{lifecycleImpact.clicks}</strong></div></div></> : <><p>This partnership can be deleted because it has no conversion history.</p><p className="mb-0 text-muted small">It remains visible as Deleted at the bottom of the Partnerships list.</p></>}{lifecycleError && <Alert variant="danger" className="mt-3 mb-0">{lifecycleError}</Alert>}</Modal.Body>
                <Modal.Footer><Button variant="secondary" disabled={lifecycleLoading} onClick={() => setShowLifecycleAction(false)}>Cancel</Button><Button variant="danger" disabled={lifecycleLoading} onClick={handleLifecycleAction}>{lifecycleLoading ? "Working…" : lifecycleImpact?.requiresArchive ? "Archive partnership" : "Delete partnership"}</Button></Modal.Footer>
            </Modal>
        </div>
    )
}

export default PartnershipAgreementDetail
